"use client";

import { useId, useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, Search, X } from "lucide-react";
import { Pill, panel } from "./ui";

export type SearchEntry = {
  kind: "course" | "lesson";
  href: string;
  title: string;
  /* The course a lesson belongs to, or what kind of course this is. */
  context: string;
  minutes?: number;
  /* Everything else worth matching on, lower-cased by the matcher. */
  extra: string;
};

const MAX_RESULTS = 8;

/* Title hits beat title-prefix hits beat description hits; courses edge out
   lessons on a tie so "memory" lands on the lesson but "team" on the path. */
function score(entry: SearchEntry, terms: string[]): number {
  const title = entry.title.toLowerCase();
  const context = entry.context.toLowerCase();
  const extra = entry.extra.toLowerCase();
  let total = 0;
  for (const t of terms) {
    if (title.startsWith(t)) total += 6;
    else if (title.includes(` ${t}`)) total += 5;
    else if (title.includes(t)) total += 4;
    else if (context.includes(t)) total += 2;
    else if (extra.includes(t)) total += 1;
    else return 0; /* every word has to match somewhere */
  }
  return total + (entry.kind === "course" ? 0.5 : 0);
}

function Highlight({ text, terms }: { text: string; terms: string[] }) {
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = text.split(new RegExp(`(${escaped.join("|")})`, "ig"));
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="rounded-sm bg-[#ffe500]/70 px-0.5 text-inherit">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

export default function LearnSearch({ entries }: { entries: SearchEntry[] }) {
  const [query, setQuery] = useState("");
  const inputId = useId();
  const listId = useId();

  const terms = useMemo(
    () => query.toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  );

  const results = useMemo(() => {
    if (terms.length === 0) return [];
    return entries
      .map((entry) => ({ entry, s: score(entry, terms) }))
      .filter((r) => r.s > 0)
      .sort((a, b) => b.s - a.s);
  }, [entries, terms]);

  const shown = results.slice(0, MAX_RESULTS);
  const searching = terms.length > 0;

  return (
    <div role="search" className="max-w-2xl">
      <label htmlFor={inputId} className="sr-only">
        Search courses and lessons
      </label>
      <div
        className="flex items-center gap-3 rounded-2xl border px-4 py-3 transition-colors focus-within:border-[#ffe500]"
        style={panel}
      >
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <input
          id={inputId}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setQuery("");
          }}
          placeholder="Search courses and lessons, e.g. memory, prompts, website"
          autoComplete="off"
          spellCheck={false}
          aria-controls={searching ? listId : undefined}
          className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
        />
        {searching ? (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>

      <p className="sr-only" role="status" aria-live="polite">
        {searching
          ? results.length === 0
            ? "No matches"
            : `${results.length} ${results.length === 1 ? "match" : "matches"}`
          : ""}
      </p>

      {searching ? (
        <div className="mt-3 overflow-hidden rounded-2xl border" style={panel}>
          {shown.length === 0 ? (
            <p className="px-5 py-6 text-[14px] text-muted-foreground">
              Nothing matches &ldquo;{query.trim()}&rdquo;. Try a shorter word, like
              &ldquo;prompt&rdquo; or &ldquo;agent&rdquo;.
            </p>
          ) : (
            <ul id={listId}>
              {shown.map(({ entry }, i) => (
                <li
                  key={entry.href}
                  className={i > 0 ? "border-t" : ""}
                  style={{ borderColor: "var(--hairline)" }}
                >
                  <Link
                    href={entry.href}
                    className="group flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-[var(--panel-strong)]"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-semibold leading-snug">
                        <Highlight text={entry.title} terms={terms} />
                      </span>
                      <span className="mt-0.5 block text-[12.5px] text-muted-foreground">
                        {entry.context}
                        {entry.minutes ? ` · ${entry.minutes} min` : ""}
                      </span>
                    </span>
                    <Pill gold={entry.kind === "course"}>
                      {entry.kind === "course" ? "Course" : "Lesson"}
                    </Pill>
                    <ArrowRight
                      className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {results.length > MAX_RESULTS ? (
            <p
              className="border-t px-5 py-3 text-[12.5px] text-muted-foreground"
              style={{ borderColor: "var(--hairline)" }}
            >
              Showing the top {MAX_RESULTS} of {results.length}. Add a word to narrow it down.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
